import configs, {buildApiRequestHeaders} from '../configs';
import {getEarthquakeIntensityLabel, getEarthquakeIntensityRank} from '../helpers/helpers-earthquake';

function parseEarthquakePayload(payload) {
    if (Array.isArray(payload)) {
        return payload;
    }
    if (payload && typeof payload.body === 'string') {
        try {
            const parsedBody = JSON.parse(payload.body);

            return Array.isArray(parsedBody) ? parsedBody : [];
        } catch (error) {
            console.warn('地震APIレスポンス body の JSON 解析に失敗しました', error);
            return [];
        }
    }
    return [];
}

function toFiniteNumber(value) {
    const number = Number(value);

    return Number.isFinite(number) ? number : null;
}

function parseEarthquakeDate(value) {
    if (!value) {
        return null;
    }

    const date = new Date(value);

    return Number.isNaN(date.getTime()) ? null : date;
}

function buildEarthquakeRequestUrl() {
    const url = new URL(configs.apiUrl.earthquake, window.location.href);

    url.searchParams.set('_', String(Date.now()));
    return url.toString();
}

function sortMunicipalities(a, b) {
    return getEarthquakeIntensityRank(b.rawIntensity) - getEarthquakeIntensityRank(a.rawIntensity) ||
        String(a.prefcode || '').localeCompare(String(b.prefcode || '')) ||
        String(a.cityname || '').localeCompare(String(b.cityname || ''), 'ja');
}

function normalizeMunicipality(municipality) {
    const latitude = toFiniteNumber(municipality.latitude),
        longitude = toFiniteNumber(municipality.longitude),
        rawIntensity = municipality.rawIntensity || municipality.maxint || '';

    return {
        citycode: String(municipality.citycode || ''),
        cityname: municipality.cityname || '',
        prefcode: municipality.prefcode || '',
        prefname: municipality.prefname || '',
        areacode: municipality.areacode || '',
        areaname: municipality.areaname || '',
        intensity: municipality.intensity || getEarthquakeIntensityLabel(rawIntensity),
        rawIntensity,
        latitude,
        longitude,
        coordinateSource: municipality.coordinateSource || '',
        citycodeMasterMatched: Boolean(municipality.citycodeMasterMatched),
        hasCoordinate: latitude !== null && longitude !== null
    };
}

function sortEarthquakes(a, b) {
    const aDate = parseEarthquakeDate(a.origintime || a.occurrence_datetime || a.report_datetime),
        bDate = parseEarthquakeDate(b.origintime || b.occurrence_datetime || b.report_datetime);

    if (aDate && bDate) {
        return bDate - aDate;
    }
    if (bDate) {
        return 1;
    }
    if (aDate) {
        return -1;
    }
    return String(b.eventid || '').localeCompare(String(a.eventid || ''));
}

function normalizeEarthquakeEvent(event) {
    const municipalities = Array.isArray(event.municipalities) ?
            event.municipalities.map(normalizeMunicipality).sort(sortMunicipalities) :
            [],
        hypocenterLatitude = toFiniteNumber(event.hypocenterLatitude),
        hypocenterLongitude = toFiniteNumber(event.hypocenterLongitude);
    const maxIntensity = municipalities.reduce((bestIntensity, municipality) =>
        getEarthquakeIntensityRank(municipality.rawIntensity) > getEarthquakeIntensityRank(bestIntensity) ?
            municipality.rawIntensity :
            bestIntensity, event.maxint || ''
    );

    return {
        eventid: String(event.eventid || ''),
        serial: String(event.serial || ''),
        title: event.title || '',
        headline: event.headline || '',
        hypocentername: event.hypocentername || '',
        hypocenterLatitude,
        hypocenterLongitude,
        hasHypocenterCoordinate: hypocenterLatitude !== null && hypocenterLongitude !== null,
        magnitude: event.magnitude || '',
        maxint: maxIntensity,
        maxIntensityLabel: event.maxIntensityLabel || getEarthquakeIntensityLabel(maxIntensity),
        origintime: event.origintime || '',
        occurrence_datetime: event.occurrence_datetime || event.origintime || '',
        report_datetime: event.report_datetime || '',
        tsunamiRisk: event.tsunamiRisk || event.forecastcomment || '',
        isCancelled: Boolean(event.isCancelled),
        municipalities,
        mapMunicipalities: municipalities.filter(municipality => municipality.hasCoordinate),
        hiddenMunicipalityCount: municipalities.filter(municipality => !municipality.hasCoordinate).length
    };
}

export function aggregateEarthquakeRecords(payload) {
    return parseEarthquakePayload(payload)
        .filter(record => record && typeof record === 'object')
        .map(normalizeEarthquakeEvent)
        .sort(sortEarthquakes);
}

export async function loadEarthquakes() {
    if (!configs.apiUrl.earthquake) {
        return [];
    }

    try {
        const response = await fetch(buildEarthquakeRequestUrl(), {
            method: 'GET',
            cache: 'no-store',
            headers: buildApiRequestHeaders({
                'Content-Type': 'application/json'
            })
        });

        if (!response.ok) {
            throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        return aggregateEarthquakeRecords(await response.json());
    } catch (error) {
        console.error('地震情報の取得に失敗しました:', error);
        return [];
    }
}

export function startEarthquakeUpdates(callback, intervalMs = 60 * 1000) {
    loadEarthquakes().then(callback);

    return setInterval(async () => {
        callback(await loadEarthquakes());
    }, intervalMs);
}

export function stopEarthquakeUpdates(intervalId) {
    if (intervalId) {
        clearInterval(intervalId);
    }
}
